"use client";
// 2026-09-14 FASE 2 P1 L-017 (web.dev CLS<0.1 2026, Vercel Academy 2026-08-21 —
// paridad DashboardSkeleton en src/app/dashboard/page.tsx:23-29): el fallback
// de Suspense/dynamic debe medir lo mismo que el contenido final ±4px o el
// resolve desplaza el layout. El logo centrado medía ~100px frente al shell
// del workspace (header 56px + mensajes + composer pill); este skeleton
// replica los mismos contenedores/espaciados por construcción:
// - header h-14 (misma altura que ChatWorkspace.tsx:459, hidden en móvil
//   igual que el original — en móvil el spacer h-2 conserva el ritmo)
// - mensajes en max-w-[840px] (ChatWorkspace.tsx:786, canvas cerrado)
// - composer pill max-w-[38rem] con padding p-3 (chat-composer.tsx:206-208)
// Se conserva el nombre del export para no tocar sus 2 importadores
// (chat/loading.tsx, chat-workspace-lazy.tsx).
// 2026-09-23 FASE 3 (un solo lenguaje skeleton 2.2s — paridad globals.css
// .skeleton/.animate-shimmer: animate-pulse era otra cadencia; el shimmer
// respeta reduced-motion vía el kill-switch global).
// 2026-09-18 FASE 3 FIX — "use client" SE MANTIENE (no migrable a RSC): el
// único hook es useT, que lee el dict desde LocaleContext/DictContext
// (client-only, src/lib/locale-context.tsx). La alternativa server exigiría
// pasar locale+dict por props desde loading.tsx con import estático de los 14
// diccionarios o carga async — un waterfall justo en el fallback de Suspense
// que debe pintar en el primer frame (LCP/CLS). Sin estado ni efectos: solo
// lectura de contexto, sin costo de hidratación interactiva.
import { useT } from "@/lib/use-i18n";

export function ChatSkeleton() {
  const t = useT();
  return (
    <div
      // 2026-10-04 fondo móvil full-bleed: UNA sola dueña de altura (page.tsx
      // h-dvh) — el skeleton va en h-full como el workspace (paridad CLS).
      className="vortixy-chat-shell relative flex h-full min-h-0 flex-col overflow-hidden bg-[var(--background)] text-foreground"
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={t("loadingTitle")}
    >
      <span className="sr-only">{t("loadingSrOnly")}</span>
      <div aria-hidden="true" className="flex min-h-0 min-w-0 flex-1 flex-col">
        {/* Header fantasma h-14 — misma altura que el header real (desktop);
            oculto en móvil igual que el original (allí flotan los botones).
            2026-09-23 FASE 3 FIX H4 (research 2026 saasframe skeleton-parity + MDN
            content-visibility: opaco bg-[var(--background)] sin /75 — el /75 dejaba
            bleed sin blur; sombra panel + containIntrinsicSize paridad con
            .vortixy-chat-message auto 128px; shimmer 2.2s SSOT intacto).
            2026-09-24 FASE 3 FIX (research 2026 MDN backdrop-filter + single-glass:
            sin shadow extra en el fantasma — 1 superficie, sin elevación; shimmer
            con motion-reduce:animate-none paridad kill-switch global). */}
        <div
          className="relative z-20 hidden h-14 shrink-0 items-center justify-between border-0 bg-transparent px-3 sm:flex sm:px-6"
          style={{
            contentVisibility: "auto",
            containIntrinsicSize: "auto 56px",
          }}
        >
          <div className="h-[20px] w-44 animate-shimmer rounded-md bg-muted/70 motion-reduce:animate-none" />
          {/* 2026-10-14 P2-2: 2 pills sin borde — paridad con el header real
              (transparente border-0, 2-4 controles); 3 pills con border-b
              flasheaban un chrome falso por 1 frame. Misma altura, 0 CLS. */}
          <div className="flex shrink-0 items-center gap-2">
            <div className="h-8 w-24 animate-shimmer rounded-full bg-muted/60 motion-reduce:animate-none" />
            <div className="size-8 animate-shimmer rounded-full bg-muted/60 motion-reduce:animate-none" />
          </div>
        </div>
        {/* Spacer móvil — mismo h-2 que el workspace (ChatWorkspace.tsx:690). */}
        <div className="h-2 shrink-0 sm:hidden" />
        {/* Mensajes fantasmas — mismo contenedor max-w-[840px] que el real.
            2026-09-23 FASE 3 FIX H4: containIntrinsicSize auto 128px paridad con
            .vortixy-chat-message (CWV CLS, sin layout-shift al resolver). */}
        <div className="vortixy-chat-scrollbar min-h-0 flex-1 overflow-y-auto px-3 sm:px-6">
          <div
            className="mx-auto flex min-h-full w-full max-w-[840px] flex-col justify-center gap-4 py-8"
            style={{
              contentVisibility: "auto",
              containIntrinsicSize: "auto 400px",
            }}
          >
            {/* 2026-09-23 FASE 3 FIX C10 (research 2026 saasframe 2026 skeleton-parity:
                burbujas con tokens hero-bento 20 + cola xs 6 — paridad con
                .vortixy-chat-bubble-user, no rounded-xl genérico).
                2026-09-24 FASE 3 FIX (research 2026 MDN contain-intrinsic-size +
                W3C css-contain-2: cada burbuja con content-visibility auto +
                containIntrinsicSize auto 64px real — sin layout-shift al resolver;
                shimmer con motion-reduce:animate-none). */}
            <div
              className="h-[16px] w-2/3 animate-shimmer self-end rounded-[var(--radius-hero-bento,20px)] rounded-br-[var(--radius-xs,6px)] bg-muted/60 p-0 motion-reduce:animate-none"
              style={{
                contentVisibility: "auto",
                containIntrinsicSize: "auto 64px",
              }}
            />
            <div
              className="h-[16px] w-1/2 animate-shimmer self-end rounded-[var(--radius-hero-bento,20px)] rounded-br-[var(--radius-xs,6px)] bg-muted/40 motion-reduce:animate-none"
              style={{
                contentVisibility: "auto",
                containIntrinsicSize: "auto 64px",
              }}
            />
            <div
              className="h-[64px] w-11/12 animate-shimmer self-start rounded-[var(--radius-hero-bento,20px)] rounded-bl-[var(--radius-xs,6px)] bg-muted/50 motion-reduce:animate-none"
              style={{
                contentVisibility: "auto",
                containIntrinsicSize: "auto 64px",
              }}
            />
            <div
              className="h-[16px] w-3/5 animate-shimmer self-start rounded-[var(--radius-hero-bento,20px)] rounded-bl-[var(--radius-xs,6px)] bg-muted/40 motion-reduce:animate-none"
              style={{
                contentVisibility: "auto",
                containIntrinsicSize: "auto 64px",
              }}
            />
          </div>
        </div>
        {/* Composer fantasma — mismo max-w-[44rem] + pill que el real
            (2026-10-04 composer unificado 44rem, pill 72px). */}
        <div className="shrink-0 bg-gradient-to-t from-[var(--background)] via-[var(--background)]/95 to-transparent px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 sm:px-6 sm:pb-6 sm:pt-4">
          <div className="mx-auto w-full max-w-[44rem] px-1 sm:px-0">
            {/* 2026-09-23 FASE 3 FIX C10 (research 2026 lovable.dev 2025-12-19: la pill real
                va SIN borde por diseño — el skeleton con ring-1 duplicaba borde; misma
                pill 28 + bg card, sin ring).
                2026-09-27 FIX radio hardcodeado → var(--radius-hero-composer, 28px)
                SSOT (paridad con la pill real del composer). */}
            <div
              style={{ borderRadius: "var(--radius-hero-composer, 28px)" }}
              className="flex w-full flex-col justify-between bg-[var(--card)] p-3"
            >
              <div className="h-[40px] w-3/4 animate-shimmer rounded-lg bg-muted/50 motion-reduce:animate-none" />
              {/* 2026-09-24 FASE 3 FIX paridad composer (research 2026 saasframe skeleton-parity:
                  el rail real es [+]+mode-pill+[send] — el fantasma llevaba 2 círculos y
                  desplazaba ±4px al resolver; pill modo h-8 w-24 al centro, sin borde). */}
              <div className="flex items-center justify-between gap-2 pt-1">
                <div className="size-8 animate-shimmer rounded-full bg-muted/60 motion-reduce:animate-none" />
                <div className="h-8 w-24 animate-shimmer rounded-full bg-muted/50 motion-reduce:animate-none" />
                <div className="size-8 animate-shimmer rounded-full bg-muted/70 motion-reduce:animate-none" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
